import { useState } from 'react'
import { useEditor } from '../store/useEditor'
import { PRODUCTS } from '../lib/products'
import { sectionsOf } from '../lib/sections'
import { bulbsForFeet, WIRE_ALLOWANCE } from '../lib/materials'
import { approvalBlockers, currentApproval, driftFrom, pickList } from '../lib/approval'
import { longDate } from '../lib/paper'
import { Section } from './bits'

/**
 * THE YES, AND WHAT HAS MOVED SINCE.
 *
 * Approving freezes a COPY of the scope. Everything below the button reads
 * from that copy and never from the live sections, which is the only way the
 * February question - what did we actually agree - has an answer.
 *
 * The refusal is a list of sentences, not a disabled button with no
 * explanation. A person told no wants to know what to go and do, and "48 ft
 * has not been measured at the house" is an instruction where a gray button is
 * a puzzle.
 *
 * NOTHING IS RESERVED HERE. Reserving against stock needs a catalog, an
 * availability count and an operation key that a retry can reuse, none of
 * which exist yet. This is the list a reservation would be made from, and it
 * says so rather than implying a warehouse has agreed to anything.
 */
export default function Approval() {
  const doc = useEditor((s) => s.doc)
  const defaults = useEditor((s) => s.defaults)
  const approve = useEditor((s) => s.approveSections)
  const [by, setBy] = useState('')
  const [note, setNote] = useState('')
  const [showPick, setShowPick] = useState(false)
  const [said, setSaid] = useState<string | null>(null)
  const [again, setAgain] = useState(false)

  const sections = sectionsOf(doc)
  if (sections.length === 0) return null

  const blockers = approvalBlockers(sections)
  const approved = currentApproval(doc)
  const drift = approved ? driftFrom(approved, sections) : []
  const product = PRODUCTS[defaults.product] ?? PRODUCTS.C9
  const list = approved ? pickList(approved, (f) => bulbsForFeet(f, product.spacingInches), WIRE_ALLOWANCE) : null

  const onApprove = () => {
    const out = approve({ by: by.trim() || 'the office', note: note.trim() })
    setSaid(out.ok ? 'Frozen. Everything below is what was agreed.' : null)
    if (out.ok) {
      setNote('')
      setAgain(false)
    }
  }

  return (
    <Section title="The scope">
      {approved && (
        <div className="apr-now">
          <p className="apr-line">
            <strong>Approved</strong> by {approved.by} on{' '}
            {longDate(approved.at.slice(0, 10))} - {approved.feet} ft across{' '}
            {approved.sections.length}{' '}
            {approved.sections.length === 1 ? 'section' : 'sections'}
            {approved.packageName ? `, ${approved.packageName}` : ''}.
          </p>
          {approved.note && <p className="hint" style={{ margin: '2px 0 0' }}>{approved.note}</p>}

          {/*
            WHAT HAS MOVED SINCE THE YES, in full, never summarized away. A
            changed scope is a conversation with the customer, so this reports
            and never quietly re-approves.
          */}
          {drift.length > 0 && (
            <div className="apr-drift">
              <p className="sec-gate warn" style={{ margin: '0 0 4px' }}>
                {drift.length === 1
                  ? 'One thing has changed since this was approved.'
                  : `${drift.length} things have changed since this was approved.`}
              </p>
              <ul className="sec-hist">
                {drift.map((d) => (
                  <li key={`${d.kind}-${d.sectionId}`}>{d.said}</li>
                ))}
              </ul>
              <p className="hint" style={{ margin: '6px 0 0' }}>
                The approved scope above is unchanged. Approve again to agree to the new one.
              </p>
            </div>
          )}
        </div>
      )}

      {blockers.length > 0 ? (
        <div className="apr-block">
          <p className="sec-gate warn" style={{ margin: '0 0 4px' }}>
            {approved ? 'This cannot be approved again yet.' : 'This cannot be approved yet.'}
          </p>
          <ul className="sec-hist">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
        WHAT THIS STEP IS FOR, and it belongs here most when the step is
        blocked. A person arriving on a refusal has never seen this screen
        working, so the only thing it has ever told them is no. Two sentences
        turn that into a reason: this is the step that makes February
        answerable.
      */}
      {blockers.length > 0 && !approved ? (
        <p className="hint apr-why">
          Approving freezes a copy of what was agreed. Everything after it, the crew's packet and
          the pick list, reads from that copy rather than from a design that can still move, which
          is how the question in February has an answer.
        </p>

      ) : approved && drift.length === 0 && !again ? (
        /*
         * NOTHING HAS MOVED, SO THERE IS NOTHING TO AGREE TO.
         *
         * A live Approve button under a scope that already stands is an
         * invitation to press it, and every press writes another frozen copy.
         * The way back is one quiet control, and it is only quiet while the
         * two things actually match.
         */
        <button className="btn ghost" onClick={() => setAgain(true)}>
          Approve it again anyway
        </button>
      ) : (
        <div className="apr-form">
          <input
            className="text"
            value={by}
            placeholder="Who is approving it"
            onChange={(e) => setBy(e.target.value)}
          />
          <input
            className="text"
            value={note}
            placeholder="What was agreed, in your words"
            onChange={(e) => setNote(e.target.value)}
          />
          <button className="btn wide primary" onClick={onApprove}>
            {approved ? 'Approve the new scope' : 'Approve this scope'}
          </button>
          {said && <p className="sec-gate ok" style={{ margin: 0 }}>{said}</p>}
        </div>
      )}

      {list && (
        <div className="apr-pick">
          <button className="btn out wide" onClick={() => setShowPick(!showPick)}>
            {showPick ? 'Hide the pick list' : 'The pick list'}
          </button>
          {showPick && (
            <>
              <table className="pick">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Section</th>
                    <th>Cut</th>
                    <th>Bulbs</th>
                    <th>Clips</th>
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.sectionId}>
                      <td>{r.n}</td>
                      <td>{r.label}</td>
                      <td>{r.feet} ft</td>
                      <td>{r.bulbs}</td>
                      <td>{r.clips}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>Total</td>
                    <td>{list.totals.feet} ft</td>
                    <td>{list.totals.bulbs}</td>
                    <td>{list.totals.clips}</td>
                  </tr>
                </tfoot>
              </table>
              <p className="hint" style={{ margin: '6px 0 0' }}>
                Cut lengths as approved, plus {list.totals.wire} ft of wire. Nothing is reserved:
                there is no stock count behind this yet.
              </p>
            </>
          )}
        </div>
      )}
    </Section>
  )
}
